import { IconButton, Modal, ModalOverlay, Tooltip, useDisclosure } from '@chakra-ui/react';
import { Editor } from '@tiptap/react';
import { ReactComponent as GenerateIcon } from 'assets/icons/design_ai.svg';
import generationUpgrade from 'assets/images/generation.png';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import {
  CLICK_ON_PAID_FEATURE,
  DESIGNAI_TEXT_GENERATOR_OPENED,
  DESIGNAI_TEXT_UPGRADE,
  UPGRADE,
} from 'constants/mixpanel';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { UserTypes, useUser, useUserGenerativeLimit } from 'modules/User';
import { GenerationDiscardModal } from 'modules/common/components/GenerationDiscardModal';
import { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { useEditor as useInfographEditor } from 'widgets/sdk';

import { GENERATION_DISCARD_TEXT } from './ImproveText.config';
import { ImproveTextModalContent } from './ImproveTextModalContent';

interface ImproveTextProps {
  editor: Editor;
  onReplaceTextContent: (newTextContent: string) => void;
  setWidgetState: (arg: WidgetState) => void;
}

export const ImproveText = memo(({ editor, onReplaceTextContent, setWidgetState }: ImproveTextProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { useSuspense: false, keyPrefix: 'textToolbar.improveText' });
  const { userId, userType, isFreeUser, isPremiumUser, isAdminUser } = useUser();

  // State for open and close improve text modal
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State for open and close discard modal
  const { isOpen: isDiscardModalOpen, onOpen: onOpenDiscardModal, onClose: onCloseDiscardModal } = useDisclosure();

  // State for open and close upgrade modal
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  const { enableEditorKeyboardShortcuts, disableEditorKeyboardShortcuts } = useInfographEditor();

  // Data for generative text credits available for premium users - skip for admin users
  const { data } = useUserGenerativeLimit(isAdminUser ? undefined : userId);

  // check is it free and premium user
  const isGatedUser = isFreeUser || isPremiumUser ? true : false;
  const generativeTextCredits = isGatedUser && data ? data : undefined;

  const label = t('buttonLabel');

  // Flag to check if a generation has been made
  const hasGeneratedText = useRef(false);

  // Handle open improve text modal (triggered by toolbar button)
  const handleOpen = useCallback(() => {
    // Free users see upgrade modal
    if (isFreeUser && generativeTextCredits !== undefined) {
      onOpenUpgradeModal();
      Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
        Feature: UPGRADE.feature.DesignAIGeneration,
        plan: getUserPlanProperty(userType),
      });
    } else {
      onOpen();
    }

    disableEditorKeyboardShortcuts();

    setWidgetState(WidgetState.Active);
    if (editor?.isEditable) editor?.setEditable(false);

    Mixpanel.track(DESIGNAI_TEXT_GENERATOR_OPENED.eventName);
  }, [
    onOpen,
    setWidgetState,
    editor,
    disableEditorKeyboardShortcuts,
    generativeTextCredits,
    onOpenUpgradeModal,
    userType,
    isFreeUser,
  ]);

  /**
   * Close the modal - if there is generated text will show a confirmation modal
   * If forceClose flag is set, will close the modal even if there is generated text
   *
   * @param forceClose boolean
   */
  const handleClose = useCallback(
    (forceClose?: boolean) => {
      // Show discard confirmation modal if there is a text generation
      if (hasGeneratedText.current && !forceClose) {
        onOpenDiscardModal();
      } else {
        hasGeneratedText.current = false;
        enableEditorKeyboardShortcuts();
        onClose();
      }
    },
    [onOpenDiscardModal, enableEditorKeyboardShortcuts, onClose],
  );

  const handleForceClose = useCallback(() => handleClose(true), [handleClose]);

  const onGenerateText = useCallback(() => (hasGeneratedText.current = true), []);
  const onDiscardText = useCallback(() => (hasGeneratedText.current = false), []);

  const onClickUpgrade = () => {
    Mixpanel.track(UPGRADE.eventName, {
      feature: UPGRADE.feature.DesignAIGeneration,
      'User Plan': getUserPlanProperty(userType),
      from: DESIGNAI_TEXT_UPGRADE.from.Modal,
    });
    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.DesignAIGeneration);
    window.open(UPGRADE_REDIRECT_LINK, '_self');
  };

  return (
    <>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        upgradePlan={UserTypes.Business}
        modalImg={generationUpgrade}
        onClickUpgrade={onClickUpgrade}
        modalType={ModalUpgradeTypeEnum.ImproveText}
      />
      <Tooltip hasArrow placement='bottom' label={label} bg='black'>
        <IconButton
          onClick={handleOpen}
          data-testid='improve-text-trigger'
          aria-label={label}
          size='sm'
          variant='icon-btn-toolbar'
          icon={<GenerateIcon />}
        >
          {label}
        </IconButton>
      </Tooltip>
      <Modal isOpen={isOpen} onClose={handleClose} portalProps={{ appendToParentPortal: false }} size='xl'>
        <ModalOverlay bg='blackAlpha.200' />
        <ImproveTextModalContent
          editor={editor}
          isOpen={isOpen}
          onClose={handleClose}
          onReplaceTextContent={onReplaceTextContent}
          onGenerateText={onGenerateText}
          onDiscardText={onDiscardText}
          onOpenUpgradeModal={onOpenUpgradeModal}
          isGatedUser={isGatedUser}
          showGeneratedTextCredits={isPremiumUser}
          userId={userId}
          userType={userType}
          generativeTextCredits={generativeTextCredits}
        />
      </Modal>
      <GenerationDiscardModal
        isOpen={isDiscardModalOpen}
        onClose={onCloseDiscardModal}
        onDiscard={handleForceClose}
        fromParent={GENERATION_DISCARD_TEXT}
      />
    </>
  );
});

ImproveText.displayName = 'ImproveTextTrigger';
